/**
 * The Crown DSL playground: a plain-English requirement goes in, the
 * production crown-dsl model writes the expression, and the real @crown/dsl
 * checker judges it.
 *
 * By default the INT8 model (~44 MB) is downloaded once, cached, and run in
 * the browser with onnxruntime-web; nothing leaves the device. A toggle sends
 * the requirement to the ai-service DSL endpoint (POST /api/nl/dsl) instead,
 * and the playground switches to it by itself when the browser cannot load
 * the model. The ai-service address is injected at build time; packages never
 * read env.
 */
import { Signal } from '@crown/signal';
import { type DslBackend, type DslEngine, type LoadProgress, loadDslEngine } from './dsl-inference';

/** ai-service base URL — injected at build time (rsbuild define), local default. */
const AI_SERVICE_URL: string = import.meta.env.AI_SERVICE_URL || 'http://localhost:3027';

/** The endpoint the server mode consumes. */
export const DSL_ENDPOINT = `${AI_SERVICE_URL}/api/nl/dsl`;

/** Example requirements (British English) that seed the input on click. */
export const EXAMPLES: readonly string[] = [
  'patients must be between 18 and 65',
  'normalise the code: trim it, then upper-case it',
  "discharge date can't be before the admission date",
  'the total of all the item costs',
  'flag the record when the status is active',
];

export const BACKEND_LABEL: Record<DslBackend, string> = {
  webgpu: 'WebGPU',
  wasm: 'WebAssembly',
};

export interface ServerAnswer {
  readonly ok: boolean;
  readonly dsl?: string;
  readonly valid?: boolean;
  readonly diagnostics?: string | null;
}

export type Outcome =
  | {
      readonly kind: 'expression';
      readonly dsl: string;
      readonly valid: boolean;
      readonly diagnostics: string | null;
      readonly chips: readonly string[];
      /** What produced it. */
      readonly footnote: string;
    }
  | { readonly kind: 'notice'; readonly variant: 'info' | 'warning' | 'danger'; readonly title: string; readonly message: string };

export interface Progress {
  readonly text: string;
  /** Share done, 0 to 1; null while the size is unknown. */
  readonly ratio: number | null;
}

function formatMb(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** Reads server-sent events: frames split on a blank line, multi-line `data:` joined. */
export async function readSse(reader: ReadableStreamDefaultReader<Uint8Array>, onEvent: (event: string, data: string) => void): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      let event = 'message';
      const data: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
      }
      if (data.length) onEvent(event, data.join('\n'));
    }
  }
}

function parseAnswer(data: string): ServerAnswer | null {
  try {
    const payload: unknown = JSON.parse(data);
    if (!payload || typeof payload !== 'object' || !('ok' in payload) || typeof payload.ok !== 'boolean') return null;
    const body = payload as Record<string, unknown>;
    return {
      ok: body.ok === true,
      dsl: typeof body.dsl === 'string' ? body.dsl : undefined,
      valid: typeof body.valid === 'boolean' ? body.valid : undefined,
      diagnostics: typeof body.diagnostics === 'string' ? body.diagnostics : null,
    };
  } catch {
    return null;
  }
}

/** Asks the ai-service for an expression; null when it cannot be reached or streams nothing usable. */
export async function askServer(description: string, signal: AbortSignal): Promise<ServerAnswer | null> {
  let response: Response;
  try {
    response = await fetch(DSL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      signal,
      body: JSON.stringify({ description }),
    });
  } catch {
    return null;
  }
  if (!response.ok || !response.body) return null;
  let answer: ServerAnswer | null = null;
  try {
    await readSse(response.body.getReader(), (event, data) => {
      if (event === 'dsl') answer = parseAnswer(data) ?? answer;
    });
  } catch {
    return null;
  }
  return answer;
}

export class PlaygroundModel {
  readonly description = new Signal.State('');
  /** Run the model in the browser; off sends the requirement to the ai-service. */
  readonly local = new Signal.State(true);
  readonly repair = new Signal.State(true);
  readonly busy = new Signal.State(false);
  readonly progress = new Signal.State<Progress | null>(null);
  readonly status = new Signal.State('');
  readonly outcome = new Signal.State<Outcome | null>(null);
  readonly copied = new Signal.State(false);
  private engine: DslEngine | null = null;
  private loading: Promise<DslEngine> | null = null;
  private controller: AbortController | null = null;

  constructor(
    private readonly load: typeof loadDslEngine = loadDslEngine,
    private readonly ask: typeof askServer = askServer,
  ) {}

  private ensureEngine(): Promise<DslEngine> {
    if (this.engine) return Promise.resolve(this.engine);
    this.loading ??= this.load({
      preferWebGpu: true,
      onProgress: (step: LoadProgress) => {
        if (step.fromCache) this.progress.set({ text: 'Loading the cached model…', ratio: null });
        else if (step.total > 0) this.progress.set({ text: `Downloading the model — ${formatMb(step.loaded)} / ${formatMb(step.total)}`, ratio: step.loaded / step.total });
        else this.progress.set({ text: `Downloading the model — ${formatMb(step.loaded)}`, ratio: null });
      },
    })
      .then((engine) => {
        this.engine = engine;
        return engine;
      })
      .catch((error: unknown) => {
        this.loading = null;
        throw error;
      });
    return this.loading;
  }

  private async inBrowser(description: string): Promise<void> {
    let engine: DslEngine;
    try {
      this.status.set(this.engine ? '' : 'Preparing the in-browser model…');
      engine = await this.ensureEngine();
    } catch {
      this.progress.set(null);
      this.status.set('');
      this.local.set(false);
      this.outcome.set({
        kind: 'notice',
        variant: 'warning',
        title: 'Could not start the in-browser model',
        message: 'Your browser could not load the local model, so the playground has switched to the ai-service. Press Generate again to use the server, or reload to retry running it locally.',
      });
      return;
    }
    this.progress.set(null);
    const backend = BACKEND_LABEL[engine.backend];
    this.status.set(`Generating locally with ${backend}…`);
    const result = await engine.generate(description, { repair: this.repair.get() });
    this.status.set('');
    this.outcome.set({
      kind: 'expression',
      dsl: result.dsl,
      valid: result.valid,
      diagnostics: result.diagnostics,
      chips: [`${backend} · in-browser`, ...(result.repaired ? ['self-repaired'] : [])],
      footnote: `Generated locally in your browser by the production crown-dsl model (onnxruntime-web, ${backend}) and validated by the Crown DSL checker — no server involved.`,
    });
  }

  private async viaServer(description: string, signal: AbortSignal): Promise<void> {
    this.status.set('Asking the Crown DSL model (ai-service)…');
    const answer = await this.ask(description, signal);
    this.status.set('');
    if (answer === null) {
      this.outcome.set({
        kind: 'notice',
        variant: 'warning',
        title: 'Could not reach the DSL endpoint',
        message: `No response from ${DSL_ENDPOINT}. Check that the ai-service is running and reachable from this origin, then try again.`,
      });
    } else if (!answer.ok || !answer.dsl) {
      this.outcome.set({
        kind: 'notice',
        variant: 'info',
        title: 'The Crown DSL model is not available',
        message: 'The ai-service responded, but no expression was produced — the model is likely disabled on this deployment. Start the ai-service with AI_DSL_NANO (or AI_SOMA) enabled and try again.',
      });
    } else {
      this.outcome.set({
        kind: 'expression',
        dsl: answer.dsl,
        valid: answer.valid === true,
        diagnostics: answer.diagnostics ?? null,
        chips: ['ai-service'],
        footnote: 'Generated by the production crown-dsl model via the Crown ai-service harness (POST /api/nl/dsl).',
      });
    }
  }

  /** Generates for the current requirement; false when there is none to generate for. */
  async run(): Promise<boolean> {
    const description = this.description.get().trim();
    if (!description) return false;
    this.controller?.abort();
    this.controller = new AbortController();
    this.busy.set(true);
    this.outcome.set(null);
    this.copied.set(false);
    try {
      if (this.local.get()) await this.inBrowser(description);
      else await this.viaServer(description, this.controller.signal);
    } catch {
      this.progress.set(null);
      this.status.set('');
      this.outcome.set({ kind: 'notice', variant: 'danger', title: 'Generation failed', message: 'Something went wrong while generating the expression. Please try again.' });
    } finally {
      this.busy.set(false);
    }
    return true;
  }

  async copy(dsl: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(dsl);
      this.copied.set(true);
      window.setTimeout(() => this.copied.set(false), 1500);
    } catch {
      // The clipboard is blocked here; the expression stays selectable on the page.
    }
  }
}
